import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { BarService } from './dal/bar.service';
import { UserService } from './dal/user.service';
import { IBar } from 'app/models/bar';

@Injectable({
  providedIn: 'root',
})
export class FcmService {
  private router = inject(Router);
  private barService = inject(BarService);
  private userService = inject(UserService);
  private alertController = inject(AlertController);

  private _bar: IBar | null = null;
  private _userId: string | null = null;
  private _done = false;
  private _listenersAttached = false;

  /**
   * Initialize push notifications. Safe to call multiple times — only the
   * first call has effect per app session. Shows a friendly pre-prompt
   * before triggering the OS permission dialog so users understand why
   * we're asking, which improves opt-in rates.
   */
  async initPush(bar: IBar, userId?: string) {
    if (Capacitor.getPlatform() === 'web') return;
    if (this._done) return;
    this._done = true;

    this._bar = bar;
    this._userId = userId ?? null;

    this.attachListenersOnce();
    await this.ensureAndroidChannel();

    try {
      const perm = await PushNotifications.checkPermissions();
      console.info('[FcmService] permission status:', perm.receive);

      if (perm.receive === 'granted') {
        console.info('[FcmService] already granted — calling register()');
        await PushNotifications.register();
        return;
      }

      if (perm.receive === 'denied') {
        console.warn('[FcmService] permission denied — user must enable in Settings');
        return;
      }

      // 'prompt' or 'prompt-with-rationale': show pre-prompt first.
      console.info('[FcmService] showing pre-prompt');
      await this.showPrePrompt();
    } catch (err) {
      console.error('[FcmService] initPush failed', err);
    }
  }

  private async ensureAndroidChannel() {
    if (Capacitor.getPlatform() !== 'android') return;
    try {
      await PushNotifications.createChannel({
        id: 'barhelper_high_v2',
        name: 'Important',
        description: 'Support replies and important alerts',
        importance: 5,
        visibility: 1,
        vibration: true,
        lights: true,
      });
    } catch (err) {
      console.error('[FcmService] createChannel failed', err);
    }
  }

  private async showPrePrompt() {
    const alert = await this.alertController.create({
      header: 'Stay updated',
      message:
        'BarHelper can notify you when support replies arrive, kegs run low, or there\'s something you should know. ' +
        'You can change this any time in your device settings.',
      buttons: [
        {
          text: 'Not now',
          role: 'cancel',
        },
        {
          text: 'Allow notifications',
          handler: async () => {
            try {
              const result = await PushNotifications.requestPermissions();
              if (result.receive === 'granted') {
                await PushNotifications.register();
              }
            } catch (err) {
              console.error('[FcmService] requestPermissions failed', err);
            }
          },
        },
      ],
    });
    await alert.present();
  }

  private attachListenersOnce() {
    if (this._listenersAttached) return;
    this._listenersAttached = true;

    PushNotifications.addListener('registration', (token: Token) => {
      console.info('[FcmService] FCM token received:', token.value.substring(0, 20) + '…');
      if (this._bar?.uBy) {
        this.barService.setNotificationToken(this._bar, token.value, this._bar.uBy);
        console.info('[FcmService] token saved to bar', this._bar.id);
      } else {
        console.warn('[FcmService] bar.uBy missing — token NOT saved to bar');
      }
      if (this._userId) {
        this.userService.setNotificationToken(this._userId, token.value);
        console.info('[FcmService] token saved to user', this._userId);
      } else {
        console.warn('[FcmService] userId missing — token NOT saved to user');
      }
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('[FcmService] registration error', error);
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.info('[FcmService] push received (foreground)', notification);
      },
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        const data = notification.notification.data;
        if (data?.route) {
          this.router.navigateByUrl(data.route);
        }
      },
    );
  }
}
